import { Loadable } from '@/components/loaders';
import { setWorkspaceInvitesPagination } from '@/features/settings/settingsSlice';
import { isCompanyAdmin, isWorkspaceAdmin } from '@/features/userInfo/hooks/useUserInfo';
import DeleteWorkspaceInvitationModal from '@/features/workspaceEdit/components/DeleteWorkspaceInvitationModal';
import { useModalContext } from '@/hooks/useModalContext';
import { FrontTable, TablePager } from '@/lib';
import { CompanyId } from '@/models/company';
import { WorkspaceId, WorkspaceInvite } from '@/models/workspace';
import { useGetWorkspaceInvitesCountQuery, useLazyGetWorkspaceInvitesQuery } from '@/services/endpoints/workspaces';
import { useAuth0 } from '@auth0/auth0-react';
import { ColumnDef } from '@tanstack/react-table';
import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getWorkspaceInvitesTableColumns } from './columns';
import { InviteTableRowType } from './types';

type WorkspaceInvitesTableProps = PropsWithChildren & {
  workspaceId: WorkspaceId;
  companyId: CompanyId;
};

export const WorkspaceInvitesTable: FC<WorkspaceInvitesTableProps> = ({ workspaceId, companyId, children }) => {
  const { user } = useAuth0();
  const { openModal } = useModalContext();

  const { t } = useTranslation('settings');

  const [getWorkspaceInvites, { data, isFetching: isInvitesFetching, isLoading: isInvitesLoading }] = useLazyGetWorkspaceInvitesQuery();
  const [invites, setInvites] = useState<WorkspaceInvite[]>([]);

  const { data: totalCount, isFetching: isTotalCountFetching } = useGetWorkspaceInvitesCountQuery({
    workspaceId,
  });

  const isAdmin = isCompanyAdmin(companyId) || isWorkspaceAdmin(workspaceId);

  const queryCallback = useCallback(
    (pageNo: number, pageSize: number) =>
      getWorkspaceInvites({
        workspaceId,
        offset: pageNo * pageSize,
        limit: pageSize,
      }),
    [workspaceId]
  );

  const handleDeleteInviteClick = useCallback(
    (invite: WorkspaceInvite) => {
      openModal({
        renderContent: DeleteWorkspaceInvitationModal,
        renderContentProps: {
          invite,
        },
        dismissable: true,
        fullWidth: false,
        fullHeight: false,
      });
    },
    [openModal]
  );

  const columns = useMemo(() => {
    const canDelete = isAdmin && Boolean(user?.sub);
    return getWorkspaceInvitesTableColumns(canDelete, canDelete ? handleDeleteInviteClick : undefined);
  }, [isAdmin, user?.sub, handleDeleteInviteClick]);

  const invitesWithId = useMemo<InviteTableRowType[]>(() => {
    return invites.filter((invite) => invite.status !== 'COMPLETE').map((invite) => ({ ...invite, rowId: invite.id }));
  }, [invites]);

  if (totalCount === 0) {
    return null;
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center justify-between'>
        <h3 className='mt-4 text-2xl font-semibold text-gray-900'>{t('workspaceUsers.pendingInvitesTitle')}</h3>
      </div>

      <TablePager
        stateSelector={(state) => state.settings.ui.workspaceInvites?.pagination}
        stateUpdater={setWorkspaceInvitesPagination}
        query={queryCallback}
        queryResponse={data ? data.items : undefined}
        setItems={setInvites}
        querying={isTotalCountFetching || isInvitesFetching}
        totalCount={totalCount || 0}
        wrapperClassNames='mb-0'
      >
        {(pager) => (
          <div className='space-y-4'>
            <Loadable isLoading={isInvitesLoading}>
              <FrontTable<InviteTableRowType>
                tableData={invitesWithId}
                columns={columns as ColumnDef<InviteTableRowType>[]}
                notFound={t('workspaceUsers.noInviteFound')}
              />
            </Loadable>
            {children}
            {pager}
          </div>
        )}
      </TablePager>
    </div>
  );
};
